/**
 * Pure geometry helpers for rendering: cells with coordinates, cell-ref resolution and
 * region borders. No rule logic lives here (that is the engine's job).
 */
import type { CellId, CellRef, GridSudokuDefinition, Topology } from "@/lib/puzzle-format/types";
import { rectCellId } from "@/lib/puzzle-format/types";

export interface GeomCell {
  id: CellId;
  row: number;
  col: number;
}

export interface Geometry {
  cells: GeomCell[];
  byId: Map<CellId, GeomCell>;
  /** Keyed by `"row,col"`: the reverse of a cell's position. */
  byPos: Map<string, GeomCell>;
  rows: number;
  cols: number;
  regionOf: Map<CellId, string>;
  /** Unit-length segments [x1, y1, x2, y2] drawn thick (region boundaries and outer edge). */
  regionBorders: [number, number, number, number][];
}

function defaultBoxes(rows: number, cols: number): { id: string; cells: [number, number][] }[] {
  const shapes: Record<string, [number, number]> = {
    "9x9": [3, 3],
    "6x6": [2, 3],
    "4x4": [2, 2],
    "8x8": [2, 4],
    "12x12": [3, 4],
    "16x16": [4, 4],
  };
  const shape = shapes[`${rows}x${cols}`];
  if (!shape) return [];
  const [bh, bw] = shape;
  const out: { id: string; cells: [number, number][] }[] = [];
  let index = 1;
  for (let br = 0; br < rows; br += bh) {
    for (let bc = 0; bc < cols; bc += bw) {
      const cells: [number, number][] = [];
      for (let r = br; r < br + bh; r += 1)
        for (let c = bc; c < bc + bw; c += 1) cells.push([r, c]);
      out.push({ id: `box${index}`, cells });
      index += 1;
    }
  }
  return out;
}

function cellAt(
  topology: Topology,
  row: number,
  col: number,
  byPos: Map<string, GeomCell>,
): CellId | null {
  if (topology.kind === "rect") {
    if (row < 0 || col < 0 || row >= topology.rows || col >= topology.cols) return null;
    return rectCellId(row, col);
  }
  return byPos.get(`${row},${col}`)?.id ?? null;
}

export function buildGeometry(definition: GridSudokuDefinition): Geometry {
  const topology = definition.topology;
  const cells: GeomCell[] = [];
  if (topology.kind === "rect") {
    for (let r = 0; r < topology.rows; r += 1) {
      for (let c = 0; c < topology.cols; c += 1)
        cells.push({ id: rectCellId(r, c), row: r, col: c });
    }
  } else {
    for (const spec of topology.cells)
      cells.push({ id: spec.id, row: spec.row ?? 0, col: spec.col ?? 0 });
  }
  const byId = new Map(cells.map((c) => [c.id, c]));
  const byPos = new Map(cells.map((c) => [`${c.row},${c.col}`, c]));
  const rows = cells.reduce((m, c) => Math.max(m, c.row + 1), 0);
  const cols = cells.reduce((m, c) => Math.max(m, c.col + 1), 0);

  const resolve = (ref: CellRef): CellId | null => {
    if (typeof ref === "string") return byId.has(ref) ? ref : null;
    return cellAt(topology, ref[0], ref[1], byPos);
  };

  // An explicit empty list means "no regions" (Latin square), absent means the standard boxes.
  const regions = topology.regions
    ? topology.regions.map((reg, i) => ({ id: reg.id ?? `region${i + 1}`, cells: reg.cells }))
    : topology.kind === "rect"
      ? defaultBoxes(topology.rows, topology.cols)
      : [];
  const regionOf = new Map<CellId, string>();
  regions.forEach((reg) => {
    for (const ref of reg.cells) {
      const id = resolve(ref);
      if (id) regionOf.set(id, reg.id);
    }
  });

  const regionBorders: [number, number, number, number][] = [];
  for (const cell of cells) {
    const mine = regionOf.get(cell.id);
    const sides: { dr: number; dc: number; seg: [number, number, number, number] }[] = [
      { dr: -1, dc: 0, seg: [cell.col, cell.row, cell.col + 1, cell.row] },
      { dr: 1, dc: 0, seg: [cell.col, cell.row + 1, cell.col + 1, cell.row + 1] },
      { dr: 0, dc: -1, seg: [cell.col, cell.row, cell.col, cell.row + 1] },
      { dr: 0, dc: 1, seg: [cell.col + 1, cell.row, cell.col + 1, cell.row + 1] },
    ];
    for (const side of sides) {
      const other = cellAt(topology, cell.row + side.dr, cell.col + side.dc, byPos);
      if (other === null) {
        regionBorders.push(side.seg); // outer edge (or a hole in a graph topology)
        continue;
      }
      // Internal boundary: drawn once, from the top/left cell of the pair.
      const owner = side.dr > 0 || side.dc > 0;
      if (owner && mine !== regionOf.get(other)) regionBorders.push(side.seg);
    }
  }
  return { cells, byId, byPos, rows, cols, regionOf, regionBorders };
}

/**
 * Borders of the regions a solver draws (chaos construction): a segment between two
 * orthogonally adjacent cells that both carry a label, and different ones. An unlabelled cell
 * gets no border yet, so a region in progress reads as open.
 */
export function labelBorders(
  geom: Geometry,
  labels: Record<CellId, string>,
): [number, number, number, number][] {
  const out: [number, number, number, number][] = [];
  for (const cell of geom.cells) {
    const mine = labels[cell.id];
    if (mine === undefined) continue;
    const below = geom.byPos.get(`${cell.row + 1},${cell.col}`);
    const right = geom.byPos.get(`${cell.row},${cell.col + 1}`);
    const belowLabel = below ? labels[below.id] : undefined;
    const rightLabel = right ? labels[right.id] : undefined;
    if (belowLabel !== undefined && belowLabel !== mine) {
      out.push([cell.col, cell.row + 1, cell.col + 1, cell.row + 1]);
    }
    if (rightLabel !== undefined && rightLabel !== mine) {
      out.push([cell.col + 1, cell.row, cell.col + 1, cell.row + 1]);
    }
  }
  return out;
}

/** Labels in a stable order, numbers numerically: the order region tints are handed out in. */
export function orderedLabels(labels: Record<CellId, string>): string[] {
  return [...new Set(Object.values(labels))].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    return a < b ? -1 : a > b ? 1 : 0;
  });
}

/**
 * How far from a cell's centre a pointer still counts as being on that cell while dragging,
 * in cell units: the inscribed circle. The four corners of a cell are dead ground, which is
 * what makes a diagonal drag possible — running from a cell to its diagonal neighbour clips
 * the corners of the two cells in between without approaching their centres, so they are not
 * picked up, and aiming at the exact corner is no longer required.
 */
export const DRAG_HIT_RADIUS = 0.5;
/** Distance between two samples of a pointer move, in cell units: a fast drag, which arrives
 * as a single long jump, must not skip the cells it flew over. */
const DRAG_SAMPLE_STEP = 0.25;
/** Bound on the sampling of one move (a drag across a whole grid stays well under it). */
const MAX_DRAG_SAMPLES = 400;

/** The cell a point is on, or null when it sits in a cell's corner, in a hole or off-grid. */
export function cellAtPoint(geom: Geometry, x: number, y: number): CellId | null {
  const row = Math.floor(y);
  const col = Math.floor(x);
  const cell = geom.byPos.get(`${row},${col}`);
  if (!cell) return null;
  const dx = x - (col + 0.5);
  const dy = y - (row + 0.5);
  return dx * dx + dy * dy <= DRAG_HIT_RADIUS * DRAG_HIT_RADIUS ? cell.id : null;
}

/** The cells a straight move from `from` to `to` runs over, in order and without repeats. */
export function cellsAlong(geom: Geometry, from: [number, number], to: [number, number]): CellId[] {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const steps = Math.min(
    MAX_DRAG_SAMPLES,
    Math.max(1, Math.ceil(Math.hypot(dx, dy) / DRAG_SAMPLE_STEP)),
  );
  const out: CellId[] = [];
  for (let i = 1; i <= steps; i += 1) {
    const id = cellAtPoint(geom, from[0] + (dx * i) / steps, from[1] + (dy * i) / steps);
    if (id && id !== out[out.length - 1]) out.push(id);
  }
  return out;
}

export function resolveRef(
  geom: Geometry,
  definition: GridSudokuDefinition,
  ref: CellRef | undefined,
): GeomCell | null {
  if (ref === undefined) return null;
  if (typeof ref === "string") return geom.byId.get(ref) ?? null;
  const topology = definition.topology;
  if (topology.kind === "rect") {
    if (ref[0] < 0 || ref[1] < 0 || ref[0] >= topology.rows || ref[1] >= topology.cols) return null;
    return geom.byId.get(rectCellId(ref[0], ref[1])) ?? null;
  }
  return geom.cells.find((c) => c.row === ref[0] && c.col === ref[1]) ?? null;
}

export function resolveRefs(
  geom: Geometry,
  definition: GridSudokuDefinition,
  refs: CellRef[] | undefined,
): GeomCell[] {
  return (refs ?? [])
    .map((ref) => resolveRef(geom, definition, ref))
    .filter((c): c is GeomCell => c !== null);
}

export function center(cell: GeomCell): [number, number] {
  return [cell.col + 0.5, cell.row + 0.5];
}

/** Mean of the cell centres: the shared edge of two neighbours, the shared corner of four. */
export function centroid(cells: GeomCell[]): [number, number] {
  const sum = cells.reduce(
    (acc, cell) => {
      const [x, y] = center(cell);
      return [acc[0] + x, acc[1] + y];
    },
    [0, 0],
  );
  return [sum[0] / cells.length, sum[1] / cells.length];
}

/**
 * Centre of a position that need not be a cell: a `[row, col]` pair on a graph topology can
 * point at a hole (a kakuro's black cells), and drawing there is legitimate for cosmetics and
 * clue-bearing constraints. A cell id must still exist.
 */
export function resolvePoint(geom: Geometry, ref: CellRef | undefined): [number, number] | null {
  if (ref === undefined) return null;
  if (typeof ref === "string") {
    const cell = geom.byId.get(ref);
    return cell ? center(cell) : null;
  }
  return [ref[1] + 0.5, ref[0] + 0.5];
}

/**
 * Inset outline of a cell set as SVG path data (unit coordinates). Each outer side of a cell
 * is drawn; a side's endpoint extends outward by `inset` when the neighbour in that direction
 * is inside the set (so segments meet at both convex and concave corners) and shrinks otherwise.
 */
export function cageOutline(cells: GeomCell[], inset = 0.08): string {
  const set = new Set(cells.map((c) => `${c.row},${c.col}`));
  const has = (r: number, c: number) => set.has(`${r},${c}`);
  const parts: string[] = [];
  for (const { row: r, col: c } of cells) {
    const xs = has(r, c - 1) ? c - inset : c + inset;
    const xe = has(r, c + 1) ? c + 1 + inset : c + 1 - inset;
    const ys = has(r - 1, c) ? r - inset : r + inset;
    const ye = has(r + 1, c) ? r + 1 + inset : r + 1 - inset;
    if (!has(r - 1, c)) parts.push(`M${xs},${r + inset}L${xe},${r + inset}`);
    if (!has(r + 1, c)) parts.push(`M${xs},${r + 1 - inset}L${xe},${r + 1 - inset}`);
    if (!has(r, c - 1)) parts.push(`M${c + inset},${ys}L${c + inset},${ye}`);
    if (!has(r, c + 1)) parts.push(`M${c + 1 - inset},${ys}L${c + 1 - inset},${ye}`);
  }
  return parts.join("");
}

/** Top-left-most cell of a set (row first, then column): used to anchor cage labels. */
export function topLeft(cells: GeomCell[]): GeomCell | undefined {
  return [...cells].sort((a, b) => a.row - b.row || a.col - b.col)[0];
}
